import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import type {
  AdminConsignorOption,
  AdminProductSearchItem,
  InventoryAddRowDraft,
} from '../types/adminInventoryAdd';
import {
  createProductCondition,
  createProductSize,
  fetchAdminConsignors,
  fetchProductConditions,
  fetchProductSizes,
  searchAdminProducts,
  submitInventoryBatch,
} from '../api/adminInventoryAdd';
import { ADMIN_INVENTORY_PATH } from '../routes/routePaths';
import { computeAutoPublicPrice } from '../utils/consignmentPricing';
import { formatMoneyDisplay, parseMoneyInputToNumber } from '../utils/moneyDisplay';
import AdminCreateProductModal from './AdminCreateProductModal';
import InventoryAddRowEditor from './InventoryAddRowEditor';

const ACCENT = '#3B82F6';
const BG_PAGE = '#F9FAFB';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(248, 250, 252, 1)',
    '& fieldset': { border: 'none' },
    '&.Mui-focused': {
      boxShadow: `0 0 0 2px ${ACCENT}`,
      backgroundColor: 'rgba(248, 250, 252, 1)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
  },
};

function toTitleCase(text: string): string {
  return text
    .split(' ')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

type PendingCatalogCreateKind = 'size' | 'condition';

interface PendingCatalogCreate {
  kind: PendingCatalogCreateKind;
  name: string;
  rowKey: string;
}

const CATALOG_ENTITY_LABELS: Record<PendingCatalogCreateKind, string> = {
  size: 'talla',
  condition: 'condición',
};

function createEmptyRow(): InventoryAddRowDraft {
  return {
    key: crypto.randomUUID(),
    productId: null,
    categoryId: null,
    selectedProduct: null,
    productSearchInput: '',
    productOptions: [],
    loadingProducts: false,
    conditions: [],
    sizes: [],
    loadingDeps: false,
    conditionId: null,
    sizeId: null,
    cost: '',
    publicPrice: '',
    autoPublicPrice: true,
    comments: '',
  };
}

function applyAutoPriceToRow(
  row: InventoryAddRowDraft,
  consignor: AdminConsignorOption | null
): InventoryAddRowDraft {
  if (!row.autoPublicPrice || !consignor) return row;
  const cost = parseMoneyInputToNumber(row.cost);
  if (!Number.isFinite(cost)) {
    return { ...row, publicPrice: '' };
  }
  return {
    ...row,
    publicPrice: formatMoneyDisplay(computeAutoPublicPrice(cost, consignor.commission)),
  };
}

function AdminAddInventoryPage(): React.ReactElement {
  const navigate = useNavigate();
  const [consignors, setConsignors] = useState<AdminConsignorOption[]>([]);
  const [consignorsError, setConsignorsError] = useState<string | null>(null);
  const [loadingConsignors, setLoadingConsignors] = useState(true);
  const [consignor, setConsignor] = useState<AdminConsignorOption | null>(null);
  const [rows, setRows] = useState<InventoryAddRowDraft[]>(() => [createEmptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [createProductModalOpen, setCreateProductModalOpen] = useState(false);
  const [createProductModalRowKey, setCreateProductModalRowKey] = useState<string | null>(null);
  const [createProductModalInitialQuery, setCreateProductModalInitialQuery] = useState('');

  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(
    () => () => {
      Object.values(searchTimers.current).forEach((id) => clearTimeout(id));
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingConsignors(true);
      setConsignorsError(null);
      const list = await fetchAdminConsignors();
      if (cancelled) return;
      setConsignors(list);
      if (list.length === 0) {
        setConsignorsError('No se pudieron cargar consignadores o la lista está vacía.');
      }
      setLoadingConsignors(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!consignor) return;
    setRows((prev) => prev.map((r) => applyAutoPriceToRow(r, consignor)));
  }, [consignor]);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, createEmptyRow()]);
  }, []);

  const removeRow = useCallback((key: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === 'n' || e.key === 'N')) {
        e.preventDefault();
        if (!consignor) return;
        addRow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [addRow, consignor]);

  const updateRow = useCallback(
    (key: string, patch: Partial<InventoryAddRowDraft>) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.key !== key) return r;
          const next = { ...r, ...patch };
          if (patch.cost !== undefined || patch.autoPublicPrice !== undefined) {
            return applyAutoPriceToRow(next, consignor);
          }
          return next;
        })
      );
    },
    [consignor]
  );

  const scheduleProductSearch = useCallback(
    (rowKey: string, query: string) => {
      const t = searchTimers.current[rowKey];
      if (t) clearTimeout(t);
      if (!query.trim()) {
        updateRow(rowKey, { productOptions: [], loadingProducts: false });
        return;
      }
      updateRow(rowKey, { loadingProducts: true });
      searchTimers.current[rowKey] = setTimeout(async () => {
        const items = await searchAdminProducts(query);
        setRows((prev) =>
          prev.map((r) =>
            r.key === rowKey ? { ...r, productOptions: items, loadingProducts: false } : r
          )
        );
      }, 350);
    },
    [updateRow]
  );

  const onSelectProduct = useCallback(
    async (rowKey: string, product: AdminProductSearchItem | null) => {
      if (!product) {
        updateRow(rowKey, {
          selectedProduct: null,
          productId: null,
          categoryId: null,
          conditions: [],
          sizes: [],
          conditionId: null,
          sizeId: null,
          loadingDeps: false,
        });
        return;
      }
      updateRow(rowKey, {
        selectedProduct: product,
        productId: product.id,
        categoryId: product.categoryId ?? null,
        productSearchInput: product.title,
        loadingDeps: true,
        conditionId: null,
        sizeId: null,
      });
      const [conditions, sizes] = await Promise.all([
        fetchProductConditions(product.id),
        fetchProductSizes(product.id),
      ]);
      setRows((prev) =>
        prev.map((r) => (r.key === rowKey ? { ...r, conditions, sizes, loadingDeps: false } : r))
      );
    },
    [updateRow]
  );

  const onCostChange = useCallback(
    (rowKey: string, value: string) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.key !== rowKey) return r;
          return applyAutoPriceToRow({ ...r, cost: value }, consignor);
        })
      );
    },
    [consignor]
  );

  const onToggleAuto = useCallback(
    (rowKey: string, checked: boolean) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.key !== rowKey) return r;
          let next = { ...r, autoPublicPrice: checked };
          if (checked && consignor) {
            next = applyAutoPriceToRow(next, consignor);
          }
          return next;
        })
      );
    },
    [consignor]
  );

  const [pendingCatalogCreate, setPendingCatalogCreate] = useState<PendingCatalogCreate | null>(
    null
  );
  const [catalogCreateName, setCatalogCreateName] = useState('');

  const handleCancelCatalogCreate = () => {
    setPendingCatalogCreate(null);
    setCatalogCreateName('');
  };

  const openSizeCreate = (rowKey: string, query: string) => {
    const name = toTitleCase(query.trim());
    setCatalogCreateName(name);
    setPendingCatalogCreate({ kind: 'size', name, rowKey });
  };

  const handleConfirmCatalogCreate = async () => {
    if (!pendingCatalogCreate) return;
    const { kind, rowKey } = pendingCatalogCreate;
    const name = kind === 'size' ? catalogCreateName.trim() : pendingCatalogCreate.name.trim();
    if (!name) return;
    setPendingCatalogCreate(null);
    setCatalogCreateName('');

    const row = rows.find((r) => r.key === rowKey);
    if (!row || !row.categoryId) return;

    if (kind === 'size') {
      const created = await createProductSize({ name, categoryId: row.categoryId });
      if (!created) return;
      setRows((prev) =>
        prev.map((r) => {
          if (r.key !== rowKey) return r;
          const sizes = r.sizes.some((s) => s.id === created.id) ? r.sizes : [...r.sizes, created];
          return { ...r, sizes, sizeId: created.id };
        })
      );
    } else {
      const created = await createProductCondition({ name, categoryId: row.categoryId });
      if (!created) return;
      setRows((prev) =>
        prev.map((r) => {
          if (r.key !== rowKey) return r;
          const conditions = r.conditions.some((c) => c.id === created.id)
            ? r.conditions
            : [...r.conditions, created];
          return { ...r, conditions, conditionId: created.id };
        })
      );
    }
  };

  const openCreateProductModal = useCallback((rowKey: string, query: string) => {
    setCreateProductModalRowKey(rowKey);
    setCreateProductModalInitialQuery(query.trim());
    setCreateProductModalOpen(true);
  }, []);

  const closeCreateProductModal = useCallback(() => {
    setCreateProductModalOpen(false);
    setCreateProductModalRowKey(null);
    setCreateProductModalInitialQuery('');
  }, []);

  const onCreatedProduct = useCallback(
    (product: AdminProductSearchItem) => {
      if (!createProductModalRowKey) {
        closeCreateProductModal();
        return;
      }
      const rowKey = createProductModalRowKey;
      setRows((prev) =>
        prev.map((r) =>
          r.key === rowKey
            ? {
                ...r,
                productOptions: r.productOptions.some((p) => p.id === product.id)
                  ? r.productOptions
                  : [product, ...r.productOptions],
              }
            : r
        )
      );
      closeCreateProductModal();
      onSelectProduct(rowKey, product).catch(() => {});
    },
    [closeCreateProductModal, createProductModalRowKey, onSelectProduct]
  );

  const handleSubmit = async () => {
    if (!consignor) {
      setSnackbar({ open: true, message: 'Selecciona un consignador.' });
      return;
    }
    const payloadRows = rows
      .map((r) => {
        if (r.productId == null || r.sizeId == null || r.conditionId == null) {
          return null;
        }
        const cost = parseMoneyInputToNumber(r.cost);
        const publicPrice = parseMoneyInputToNumber(r.publicPrice);
        if (!Number.isFinite(cost) || !Number.isFinite(publicPrice)) return null;
        return {
          productId: r.productId,
          sizeId: r.sizeId,
          conditionId: r.conditionId,
          cost,
          publicPrice,
          comments: r.comments.trim(),
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);

    if (payloadRows.length === 0) {
      setSnackbar({
        open: true,
        message: 'Completa al menos una fila con producto, talla, condición y montos válidos.',
      });
      return;
    }

    setSubmitting(true);
    const result = await submitInventoryBatch({ consignorId: consignor.id, rows: payloadRows });
    setSubmitting(false);
    if (result.ok) {
      navigate(ADMIN_INVENTORY_PATH);
    } else {
      setSnackbar({ open: true, message: result.error });
    }
  };

  const canEditRows = Boolean(consignor);

  const renderAddRows = (layout: 'desktop' | 'mobile') =>
    rows.map((row, index) => (
      <InventoryAddRowEditor
        key={row.key}
        row={row}
        index={index}
        layout={layout}
        canEdit={canEditRows}
        canRemove={rows.length > 1}
        onUpdate={(patch) => updateRow(row.key, patch)}
        onSelectProduct={(product) => {
          onSelectProduct(row.key, product).catch(() => {});
        }}
        onSearchProduct={(query) => scheduleProductSearch(row.key, query)}
        onOpenCreateProduct={() => openCreateProductModal(row.key, row.productSearchInput)}
        onOpenCreateSize={(query) => openSizeCreate(row.key, query)}
        onOpenCreateCondition={(query) =>
          setPendingCatalogCreate({
            kind: 'condition',
            name: toTitleCase(query),
            rowKey: row.key,
          })
        }
        onCostChange={(value) => onCostChange(row.key, value)}
        onToggleAuto={(checked) => onToggleAuto(row.key, checked)}
        onRemove={() => removeRow(row.key)}
      />
    ));

  return (
    <Box
      sx={{
        bgcolor: BG_PAGE,
        minHeight: '100%',
        mx: -4,
        mt: -4,
        px: { xs: 2, sm: 4 },
        py: 3,
        mb: -4,
      }}
    >
      <Box sx={{ maxWidth: 1680, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton
            component={Link}
            to={ADMIN_INVENTORY_PATH}
            aria-label="Volver al inventario"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              Agregar al inventario
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Entrada por lote para nuevos artículos en consignación.
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Consignador
          </Typography>
          {loadingConsignors ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Cargando consignadores…
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              options={consignors}
              value={consignor}
              onChange={(_, v) => setConsignor(v)}
              getOptionLabel={(c) => c.fullName.trim() || 'Sin nombre'}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField
                  id={params.id}
                  disabled={params.disabled}
                  fullWidth={params.fullWidth}
                  size={params.size}
                  placeholder="Buscar consignador…"
                  sx={inputSx}
                  slotProps={{
                    inputLabel: params.InputLabelProps,
                    htmlInput: params.inputProps,
                    input: {
                      ref: params.InputProps.ref,
                      className: params.InputProps.className,
                      endAdornment: params.InputProps.endAdornment,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonSearchIcon sx={{ color: 'action.active', ml: 0.5 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          )}
          {consignorsError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {consignorsError}
            </Alert>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              p: 2,
              pb: 1,
              overflowX: 'auto',
              bgcolor: 'rgba(248, 250, 252, 1)',
            }}
          >
            {renderAddRows('desktop')}
          </Box>

          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2, pb: 1 }}>
            {renderAddRows('mobile')}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Button
              startIcon={<AddIcon />}
              onClick={addRow}
              disabled={!canEditRows}
              fullWidth
              sx={{ color: ACCENT, fontWeight: 600, textTransform: 'none', width: { sm: 'auto' } }}
            >
              Agregar fila
            </Button>
            <Box
              sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate(ADMIN_INVENTORY_PATH)}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  display: { xs: 'inline-flex', md: 'none' },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                disabled={submitting || !canEditRows}
                onClick={() => {
                  handleSubmit().catch(() => {});
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 4,
                  py: 1.25,
                  width: { xs: '100%', sm: 'auto' },
                  bgcolor: '#000',
                  '&:hover': { bgcolor: '#1e293b' },
                }}
              >
                {submitting ? 'Enviando…' : 'Registrar en inventario'}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
          El precio público puede calcularse automáticamente según la comisión del consignador o
          editarse a mano si desactivas &quot;Auto&quot; en la fila.
        </Alert>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Atajo: Alt + N para agregar fila (requiere consignador)
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <AdminCreateProductModal
        open={createProductModalOpen}
        initialModelQuery={createProductModalInitialQuery}
        onClose={closeCreateProductModal}
        onCreated={onCreatedProduct}
      />

      <Dialog
        open={Boolean(pendingCatalogCreate)}
        onClose={handleCancelCatalogCreate}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {pendingCatalogCreate?.kind === 'size' ? 'Nueva talla' : 'Confirmar creación'}
        </DialogTitle>
        <DialogContent>
          {pendingCatalogCreate?.kind === 'size' ? (
            <TextField
              autoFocus
              fullWidth
              label="Título"
              placeholder="Ej. 10.5"
              value={catalogCreateName}
              onChange={(e) => setCatalogCreateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && catalogCreateName.trim()) {
                  e.preventDefault();
                  handleConfirmCatalogCreate().catch(() => {});
                }
              }}
              sx={{ mt: 1, ...inputSx }}
            />
          ) : (
            <Typography>
              ¿Deseas crear{' '}
              {pendingCatalogCreate ? CATALOG_ENTITY_LABELS[pendingCatalogCreate.kind] : ''}{' '}
              <strong>&ldquo;{pendingCatalogCreate?.name}&rdquo;</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelCatalogCreate}
            color="inherit"
            sx={{ textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              handleConfirmCatalogCreate().catch(() => {});
            }}
            variant="contained"
            disabled={Boolean(pendingCatalogCreate?.kind === 'size' && !catalogCreateName.trim())}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#2563EB' },
            }}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminAddInventoryPage;
